import { useEffect, useState, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Button, Card, Typography, Spin, Result, Tag, theme } from 'antd'
import { LoginOutlined, ClockCircleOutlined, TeamOutlined } from '@ant-design/icons'
import { validateInvite, InviteValidateResponse } from '@/api/invites'
import { ROLE_LABELS } from '@/utils/userRoles'

const { Title, Text, Paragraph } = Typography

type PageState = 'loading' | 'valid' | 'expired' | 'error'

function InviteLandingPage() {
  const { token } = useParams<{ token: string }>()
  const { token: themeToken } = theme.useToken()
  const [pageState, setPageState] = useState<PageState>('loading')
  const [invite, setInvite] = useState<InviteValidateResponse | null>(null)
  const fetched = useRef(false)

  useEffect(() => {
    if (fetched.current || !token) return
    fetched.current = true

    validateInvite(token)
      .then((data) => {
        if (data.valid) {
          setInvite(data)
          setPageState('valid')
        } else {
          setPageState('expired')
        }
      })
      .catch(() => {
        setPageState('error')
      })
  }, [token])

  const handleLogin = () => {
    // Store invite token so it can be applied after OIDC callback
    if (token) {
      localStorage.setItem('medcifra-invite-token', token)
    }
    window.location.href = '/api/v1/auth/oidc/login'
  }

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: themeToken.colorBgLayout,
  }

  const logoBlock = (
    <div style={{ textAlign: 'center', marginBottom: 24 }}>
      <img
        src="/logo-icon.svg"
        alt="Медцифра"
        style={{ height: 64, marginBottom: 16 }}
      />
      <Title level={2} style={{ marginBottom: 4 }}>Медцифра</Title>
      <Text type="secondary">Платформа медицинской аналитики</Text>
    </div>
  )

  // --- Loading ---
  if (pageState === 'loading') {
    return (
      <div style={{ ...containerStyle, flexDirection: 'column', gap: 16 }}>
        <Spin size="large" />
        <Text type="secondary">Проверяем приглашение...</Text>
      </div>
    )
  }

  // --- Expired / used ---
  if (pageState === 'expired') {
    return (
      <div style={containerStyle}>
        <Card style={{ width: 440, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          {logoBlock}
          <Result
            status="warning"
            icon={<ClockCircleOutlined />}
            title="Ссылка устарела"
            subTitle="Приглашение истекло или уже было использовано. Запросите новое приглашение у администратора вашей организации."
            style={{ padding: '16px 0' }}
          />
          <Link to="/login">
            <Button type="default" block>
              Перейти на страницу входа
            </Button>
          </Link>
        </Card>
      </div>
    )
  }

  // --- Network / unexpected error ---
  if (pageState === 'error') {
    return (
      <div style={containerStyle}>
        <Card style={{ width: 440, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          {logoBlock}
          <Result
            status="error"
            title="Не удалось проверить приглашение"
            subTitle="Произошла ошибка. Попробуйте перейти по ссылке ещё раз или обратитесь к администратору."
            style={{ padding: '16px 0' }}
          />
          <Link to="/login">
            <Button type="default" block>
              Перейти на страницу входа
            </Button>
          </Link>
        </Card>
      </div>
    )
  }

  // --- Valid invite ---
  const roleName = invite?.role ? (ROLE_LABELS[invite.role] || invite.role) : null

  return (
    <div style={containerStyle}>
      <Card style={{ width: 440, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        {logoBlock}

        <div
          style={{
            background: themeToken.colorBgTextHover,
            borderRadius: themeToken.borderRadiusLG,
            padding: '20px 24px',
            marginBottom: 24,
            textAlign: 'center',
          }}
        >
          <TeamOutlined style={{ fontSize: 28, color: themeToken.colorPrimary, marginBottom: 8 }} />
          <Paragraph style={{ marginBottom: 4, fontSize: 16 }}>
            Вас пригласили в организацию
          </Paragraph>
          <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>
            {invite?.org_name || 'Организация'}
          </Title>
          {roleName && (
            <Tag color="blue" style={{ fontSize: 14, padding: '2px 12px' }}>
              {roleName}
            </Tag>
          )}
        </div>

        {invite?.email && (
          <Paragraph type="secondary" style={{ textAlign: 'center', marginBottom: 24 }}>
            Приглашение для <Text strong>{invite.email}</Text>
          </Paragraph>
        )}

        <Button
          type="primary"
          size="large"
          block
          icon={<LoginOutlined />}
          onClick={handleLogin}
        >
          Войти через Медцифра ID
        </Button>
      </Card>
    </div>
  )
}

export default InviteLandingPage
